import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, isSuperAdmin } from "../../contexts/AuthContext";
import Modal from "../../components/ui/Modal";
import TagInput from "../../components/ui/TagInput";
import { FIELD_BY_KEY, channelKey, emptyChannel, valuesOf } from "../../inventory/fields";
import { CURRENCIES, formatReach, parseReach, uniqueCaseless } from "../../inventory/parse";
import { formatDateTime, timeAgo } from "../../inventory/time";

const TITLES = { add: "Add channel", edit: "Edit channel", duplicate: "Duplicate channel" };

// Everything already in use for a field, for suggestions and option lists
function usedValues(channels, key) {
  return uniqueCaseless(channels.flatMap((c) => valuesOf(c, key))).sort((a, b) =>
    a.localeCompare(b)
  );
}

function CheckGroup({ field, value, extra, onChange }) {
  const options = uniqueCaseless([...(field.options || []), ...extra, ...value]);
  const on = new Set(value.map((v) => v.toLowerCase()));
  return (
    <div className="check-group" role="group" aria-label={field.label}>
      {options.map((opt) => {
        const checked = on.has(opt.toLowerCase());
        return (
          <label key={opt} className={`check-chip${checked ? " is-on" : ""}`}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() =>
                onChange(
                  checked ? value.filter((v) => v.toLowerCase() !== opt.toLowerCase()) : [...value, opt]
                )
              }
            />
            {opt}
          </label>
        );
      })}
    </div>
  );
}

export default function ChannelDialog({ mode, channel, channels, onSave, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => {
    const base = { ...emptyChannel(), ...(channel || {}) };
    return {
      ...base,
      channelName: mode === "duplicate" ? `${base.channelName} (copy)` : base.channelName,
      cpmText: base.cpm == null ? "" : String(base.cpm),
      reachText: base.reach == null ? "" : formatReach(base.reach),
    };
  });
  const [errors, setErrors] = useState({});

  const suggest = useMemo(
    () => ({
      partner: usedValues(channels, "partner"),
      platform: usedValues(channels, "platform"),
      territories: usedValues(channels, "territories"),
      genres: usedValues(channels, "genres"),
      formats: usedValues(channels, "formats"),
      adFormats: usedValues(channels, "adFormats"),
      adTypes: usedValues(channels, "adTypes"),
    }),
    [channels]
  );

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const setFromEvent = (key) => (e) => set(key)(e.target.value);

  const validate = () => {
    const e = {};
    const name = form.channelName.trim();
    const partner = form.partner.trim();
    if (!name) e.channelName = "Enter a channel name.";
    if (!partner) e.partner = "Enter the partner.";
    // Same partner and name is fine in another territory or format
    const key = channelKey({ ...form, channelName: name, partner });
    if (
      name &&
      partner &&
      channels.some((c) => (mode !== "edit" || c.id !== channel.id) && channelKey(c) === key)
    ) {
      e.channelName = `${partner} already has ${name} for this territory and format.`;
    }
    if (form.cpmText.trim() && !(parseFloat(form.cpmText.replace(",", ".")) >= 0)) {
      e.cpmText = "Enter a number, like 23.5.";
    }
    if (form.reachText.trim() && parseReach(form.reachText) == null) {
      e.reachText = "Enter a number or 500K, 30M, 1.2B.";
    }
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.values(e).some(Boolean)) {
      setErrors(e);
      return;
    }
    const { cpmText, reachText, id, updatedAt, ...rest } = form;
    onSave({
      ...rest,
      channelName: form.channelName.trim(),
      partner: form.partner.trim(),
      platform: form.platform.trim(),
      revshare: form.revshare.trim(),
      revshareType: form.revshareType.trim(),
      contactName: form.contactName.trim(),
      contactEmail: form.contactEmail.trim(),
      cpm: cpmText.trim() ? parseFloat(cpmText.replace(",", ".")) : null,
      reach: reachText.trim() ? parseReach(reachText) : null,
    });
  };

  const reachPreview = parseReach(form.reachText);

  return (
    <Modal
      title={TITLES[mode]}
      wide
      onClose={onClose}
      footer={
        <>
          {mode === "edit" && (
            <span className="dialog-meta">
              {channel.updatedAt ? (
                <span title={formatDateTime(channel.updatedAt)}>
                  Updated {timeAgo(channel.updatedAt)}
                  {channel.updatedBy && <> by {channel.updatedBy}</>}
                </span>
              ) : (
                <span>From the sample data</span>
              )}
              {isSuperAdmin(user) && (
                <Link to={`/recent?channel=${encodeURIComponent(channel.id)}`}>View history</Link>
              )}
            </span>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="channel-form" className="btn-primary">
            {mode === "edit" ? "Save changes" : "Add channel"}
          </button>
        </>
      }
    >
      <form id="channel-form" className="form-grid" onSubmit={submit} noValidate>
        <div className="form-field">
          <label htmlFor="cf-name">
            Channel name <span className="req">*</span>
          </label>
          <input
            id="cf-name"
            className={`form-input${errors.channelName ? " has-error" : ""}`}
            value={form.channelName}
            onChange={setFromEvent("channelName")}
          />
          {errors.channelName && <span className="form-error">{errors.channelName}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="cf-partner">
            Partner <span className="req">*</span>
          </label>
          <input
            id="cf-partner"
            className={`form-input${errors.partner ? " has-error" : ""}`}
            value={form.partner}
            onChange={setFromEvent("partner")}
            list="cf-partner-list"
          />
          <datalist id="cf-partner-list">
            {suggest.partner.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          {errors.partner && <span className="form-error">{errors.partner}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="cf-territories">Territory</label>
          <TagInput
            id="cf-territories"
            value={form.territories}
            onChange={set("territories")}
            suggestions={suggest.territories}
          />
        </div>

        <div className="form-field">
          <label htmlFor="cf-genres">Genre</label>
          <TagInput
            id="cf-genres"
            value={form.genres}
            onChange={set("genres")}
            suggestions={suggest.genres}
          />
        </div>

        <div className="form-field span-2">
          <span className="form-label">Format</span>
          <CheckGroup field={FIELD_BY_KEY.formats} value={form.formats} extra={suggest.formats} onChange={set("formats")} />
        </div>

        <div className="form-field">
          <span className="form-label">Ad format</span>
          <CheckGroup field={FIELD_BY_KEY.adFormats} value={form.adFormats} extra={suggest.adFormats} onChange={set("adFormats")} />
        </div>

        <div className="form-field">
          <span className="form-label">Ad type</span>
          <CheckGroup field={FIELD_BY_KEY.adTypes} value={form.adTypes} extra={suggest.adTypes} onChange={set("adTypes")} />
        </div>

        <div className="form-field">
          <label htmlFor="cf-cpm">CPM</label>
          <div className="input-row">
            <input
              id="cf-cpm"
              className={`form-input${errors.cpmText ? " has-error" : ""}`}
              inputMode="decimal"
              value={form.cpmText}
              onChange={setFromEvent("cpmText")}
            />
            <select
              className="form-input"
              aria-label="Currency"
              value={form.currency}
              onChange={setFromEvent("currency")}
            >
              {uniqueCaseless([...CURRENCIES, form.currency]).map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          {errors.cpmText && <span className="form-error">{errors.cpmText}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="cf-reach">Reach</label>
          <input
            id="cf-reach"
            className={`form-input${errors.reachText ? " has-error" : ""}`}
            value={form.reachText}
            onChange={setFromEvent("reachText")}
          />
          {errors.reachText ? (
            <span className="form-error">{errors.reachText}</span>
          ) : (
            reachPreview != null && (
              <span className="form-hint">{reachPreview.toLocaleString("en-US")} viewers</span>
            )
          )}
        </div>

        <div className="form-field span-2">
          <label htmlFor="cf-platform">Platform / network / owner</label>
          <input
            id="cf-platform"
            className="form-input"
            value={form.platform}
            onChange={setFromEvent("platform")}
            list="cf-platform-list"
          />
          <datalist id="cf-platform-list">
            {suggest.platform.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </div>

        <div className="form-field">
          <label htmlFor="cf-revshare">Revshare</label>
          <input id="cf-revshare" className="form-input" value={form.revshare} onChange={setFromEvent("revshare")} />
        </div>

        <div className="form-field">
          <label htmlFor="cf-commercial-model">Commercial model</label>
          <input
            id="cf-commercial-model"
            className="form-input"
            value={form.revshareType}
            onChange={setFromEvent("revshareType")}
          />
        </div>

        <div className="form-field">
          <label htmlFor="cf-contact">Contact name</label>
          <input
            id="cf-contact"
            className="form-input"
            value={form.contactName}
            onChange={setFromEvent("contactName")}
          />
        </div>

        <div className="form-field">
          <label htmlFor="cf-email">Contact email</label>
          <input
            id="cf-email"
            type="email"
            className="form-input"
            value={form.contactEmail}
            onChange={setFromEvent("contactEmail")}
          />
        </div>
      </form>
    </Modal>
  );
}
